import React, { useEffect, useState } from "react";
import { getProductCategories, saveProductCategory } from "../../components/Api/GmsProductCategory";
import Select from "react-select";
import { Button, Row, Col, FormGroup, Label, Input, Form } from "reactstrap";
import Swal from "sweetalert2";
import { Eye } from "lucide-react";
import { ClipLoader } from "react-spinners";
import { 
  getUserPermissions, 
  hasPagePermissionWithAdmin, 
  hasEditPermissionWithAdmin, 
  hasDeletePermissionWithAdmin 
} from "../../components/Permission/PermissionService";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  Product_Category_Id: 0,
  Product_Category_NameEn: "",
  Product_Category_NameAr: "",
  Status: true,
  CreatedOn: new Date().toISOString(),
  CreatedBy: Number(localStorage.getItem("userId")) || 1,
  ModifiedOn: new Date().toISOString(),
  ModifiedBy: Number(localStorage.getItem("userId")) || 1,
};

const emptyFilter = {
  Product_Category_NameEn: "",
  Product_Category_NameAr: "",
  Status: null,
};

export default function ProductCategoryPage() {
  const [productCategories, setProductCategories] = useState([]);
  const [allProductCategories, setAllProductCategories] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [showFilters, setShowFilters] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);
  
  // Permission states
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  // Load permissions first
  useEffect(() => {
    loadPermissions();
  }, []);

  // Load data after permissions are loaded
  useEffect(() => {
    if (!permissionsLoading && canView) {
      loadProductCategories();
    }
  }, [permissionsLoading, canView, page]);

  const loadPermissions = async () => {
    try {
      const userPermissions = await getUserPermissions();
      setPermissions(userPermissions);
      
      // Check permissions for this page
      const hasView = hasPagePermissionWithAdmin(userPermissions, '/GmsProductCategory');
      const hasEdit = hasEditPermissionWithAdmin(userPermissions, '/GmsProductCategory');
      const hasDelete = hasDeletePermissionWithAdmin(userPermissions, '/GmsProductCategory');
      
      setCanView(hasView);
      setCanEdit(hasEdit);
      setCanDelete(hasDelete);
      
      // If no view permission, redirect to dashboard
      if (!hasView) {
        Swal.fire("Access Denied", "You don't have permission to view this page.", "error").then(() => {
          window.location.href = '/Dashboard';
        });
      }
    } catch (error) {
      console.error("Error loading permissions:", error);
      Swal.fire("Error", "Failed to load permissions. Redirecting to dashboard.", "error").then(() => {
        window.location.href = '/Dashboard';
      });
    } finally {
      setPermissionsLoading(false);
    }
  };

  const loadProductCategories = async (filterData = emptyFilter) => {
    setLoading(true);
    try {
      const data = await getProductCategories(filterData);
      // Normalize data: convert camelCase from API to handle both formats
      const normalizedData = data.map(category => ({
        // Keep both formats for compatibility
        product_Category_Id: category.product_Category_Id || category.Product_Category_Id,
        product_Category_NameEn: category.product_Category_NameEn || category.Product_Category_NameEn || '',
        product_Category_NameAr: category.product_Category_NameAr || category.Product_Category_NameAr || '',
        status: category.status !== undefined ? category.status : category.Status,
        createdOn: category.createdOn || category.CreatedOn,
        createdBy: category.createdBy || category.CreatedBy,
        modifiedOn: category.modifiedOn || category.ModifiedOn,
        modifiedBy: category.modifiedBy || category.ModifiedBy,
        // Also keep PascalCase for backward compatibility
        Product_Category_Id: category.product_Category_Id || category.Product_Category_Id,
        Product_Category_NameEn: category.product_Category_NameEn || category.Product_Category_NameEn || '',
        Product_Category_NameAr: category.product_Category_NameAr || category.Product_Category_NameAr || '',
        Status: category.status !== undefined ? category.status : category.Status,
      }));
      setAllProductCategories(normalizedData);
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = normalizedData.slice(startIndex, endIndex);
      setProductCategories(paginatedData);
      setTotalCount(normalizedData.length);
    } catch (error) {
      Swal.fire("Error", "Failed to load product categories: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Set default active status based on permissions
  useEffect(() => {
    if (!isEditing && canEdit && !canDelete) {
      setForm(prev => ({
        ...prev,
        Status: true
      }));
    }
  }, [isEditing, canEdit, canDelete]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter({
      ...filter,
      [name]: value === "" ? null : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = {
        ...form,
        ModifiedOn: new Date().toISOString(),
        ModifiedBy: Number(localStorage.getItem("userId")) || 1,
      };
      const result = await saveProductCategory(formData);
      Swal.fire("Success", result.message, "success");
      setForm(defaultForm);
      setIsEditing(false);
      loadProductCategories(filter);
    } catch (error) {
      Swal.fire("Error", "Failed to save product category: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (category) => {
    const categoryId = category.product_Category_Id || category.Product_Category_Id;
    const nameEn = category.product_Category_NameEn || category.Product_Category_NameEn || 'N/A';
    const nameAr = category.product_Category_NameAr || category.Product_Category_NameAr || 'N/A';
    const isActive = category.status !== undefined ? category.status : category.Status;
    
    setViewLoadingRow(categoryId);
    try {
      Swal.fire({
        title: nameEn,
        html: `
          <div class="text-left">
            <div class="mb-3">
              <h6 class="mb-2">Basic Information</h6>
              <p><strong>Name (English):</strong> ${nameEn}</p>
              <p><strong>Name (Arabic):</strong> ${nameAr}</p>
              <p><strong>Status:</strong> ${isActive ? "Active" : "Inactive"}</p>
            </div>
          </div>
        `,
        icon: "info",
        width: '600px',
        customClass: {
          container: 'custom-swal-container',
          popup: 'custom-swal-popup',
          content: 'custom-swal-content'
        }
      });
    } catch (error) {
      Swal.fire("Error", "Failed to view product category details", "error");
    } finally {
      setViewLoadingRow(null);
    }
  };

  const handleEdit = (category) => {
    // Normalize the category data to PascalCase for the form
    setForm({
      Product_Category_Id: category.product_Category_Id || category.Product_Category_Id,
      Product_Category_NameEn: category.product_Category_NameEn || category.Product_Category_NameEn || '',
      Product_Category_NameAr: category.product_Category_NameAr || category.Product_Category_NameAr || '',
      Status: category.status !== undefined ? category.status : category.Status,
      CreatedOn: category.createdOn || category.CreatedOn || new Date().toISOString(),
      CreatedBy: category.createdBy || category.CreatedBy || Number(localStorage.getItem("userId")) || 1,
      ModifiedOn: new Date().toISOString(),
      ModifiedBy: Number(localStorage.getItem("userId")) || 1,
    });
    setIsEditing(true);
    window.scrollTo(0, 0);
  };

  const handleCancelEdit = () => {
    setForm(defaultForm);
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    setPage(1);
    loadProductCategories(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    loadProductCategories(emptyFilter);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Generate options for searchable dropdowns
  const categoryNameEnOptions = allProductCategories.map(c => ({
    value: c.product_Category_NameEn || c.Product_Category_NameEn || '',
    label: c.product_Category_NameEn || c.Product_Category_NameEn || ''
  }));

  const categoryNameArOptions = allProductCategories.map(c => ({
    value: c.product_Category_NameAr || c.Product_Category_NameAr || '',
    label: c.product_Category_NameAr || c.Product_Category_NameAr || ''
  }));

  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  const handleSelectChange = (selectedOption, field) => {
    setFilter({ 
      ...filter, 
      [field]: selectedOption ? selectedOption.value : null 
    });
  };

  // Show loading spinner while permissions are loading
  if (permissionsLoading) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-body text-center py-5">
              <ClipLoader size={50} color="#556ee6" />
              <p className="mt-3">Loading permissions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if no view permission
  if (!canView) {
    return null;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">Product Category Management</h4>
        </div>

        {/* Form Section - Only show if user has edit permission */}
        {canEdit && (
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0">
                {isEditing ? "Edit Product Category" : "Product Category Details"}
              </h5>
            </div>
            <div className="card-body">
              <Form onSubmit={handleSubmit}>
                <Row className="mb-3">
                  <Col md={6}>
                    <FormGroup>
                      <Label for="Product_Category_NameEn">Name (English) <span className="text-danger">*</span></Label>
                      <Input
                        type="text"
                        name="Product_Category_NameEn"
                        id="Product_Category_NameEn"
                        placeholder="Name in English"
                        value={form.Product_Category_NameEn}
                        onChange={handleFormChange}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="Product_Category_NameAr">Name (Arabic)</Label>
                      <Input
                        type="text"
                        name="Product_Category_NameAr"
                        id="Product_Category_NameAr"
                        placeholder="Name in Arabic"
                        value={form.Product_Category_NameAr}
                        onChange={handleFormChange}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={12}>
                    <FormGroup className="mb-4">
                      <div className="form-check form-switch">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="product_category_status"
                          name="Status"
                          checked={form.Status}
                          onChange={handleFormChange}
                          disabled={!canDelete}
                        />
                        <label className="form-check-label" htmlFor="product_category_status">
                          {form.Status ? "Active" : "Inactive"}
                        </label>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end">
                  <Button 
                    color="success"
                    className="me-2" 
                    type="submit" 
                    disabled={loading}
                  >
                    <i className="bx bx-check-circle me-2"></i>
                    {loading ? "Saving..." : (isEditing ? "Update Product Category" : "Save Product Category")}
                  </Button>
                  {isEditing && (
                    <Button 
                      color="danger" 
                      type="button" 
                      onClick={handleCancelEdit}
                    >
                      <i className="bx bx-x-circle me-2"></i>
                      Cancel
                    </Button>
                  )}
                </div>
              </Form>
            </div>
          </div>
        )}

        {/* Filter Section */}
        <div className="card mb-4">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">Filter Product Categories</h5>
              <button
                className="btn btn-primary d-flex align-items-center"
                onClick={() => setShowFilters(!showFilters)}
              >
                <i className="bx bx-filter me-2"></i>
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>
          {showFilters && (
            <div className="card-body">
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <label className="form-label">Name (English)</label>
                  <Select
                    value={categoryNameEnOptions.find(option => option.value === filter.Product_Category_NameEn)}
                    onChange={(option) => handleSelectChange(option, 'Product_Category_NameEn')}
                    options={categoryNameEnOptions}
                    isClearable
                    placeholder="Select Name (English)"
                    styles={selectStyles}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Name (Arabic)</label>
                  <Select
                    value={categoryNameArOptions.find(option => option.value === filter.Product_Category_NameAr)}
                    onChange={(option) => handleSelectChange(option, 'Product_Category_NameAr')}
                    options={categoryNameArOptions}
                    isClearable
                    placeholder="Select Name (Arabic)"
                    styles={selectStyles}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Status</label>
                  <Select
                    value={filter.Status !== null 
                      ? statusOptions.find(option => option.value === filter.Status)
                      : null}
                    onChange={(option) => handleSelectChange(option, 'Status')}
                    options={statusOptions}
                    isClearable
                    placeholder="Select Status"
                    styles={selectStyles}
                  />
                </div>
              </div>

              <div className="d-flex justify-content-end">
                <button 
                  className="btn btn-primary me-2" 
                  onClick={handleApplyFilter} 
                  disabled={loading}
                >
                  <i className="bx bx-search me-2"></i>
                  Apply Filter
                </button>
                <button 
                  className="btn btn-warning" 
                  onClick={handleClearFilter}
                >
                  <i className="bx bx-refresh me-2"></i>
                  Clear Filter
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Table Section */}
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">Product Category List ({totalCount})</h5>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Name (English)</th>
                      <th>Name (Arabic)</th>
                      <th>Active</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productCategories.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center">No product categories found</td>
                      </tr>
                    ) : (
                      productCategories.map((category, index) => {
                        const categoryId = category.product_Category_Id || category.Product_Category_Id;
                        const nameEn = category.product_Category_NameEn || category.Product_Category_NameEn || '';
                        const nameAr = category.product_Category_NameAr || category.Product_Category_NameAr || '';
                        const isActive = category.status !== undefined ? category.status : category.Status;
                        
                        return (
                          <tr key={categoryId}>
                            <td>{(page - 1) * pageSize + index + 1}</td>
                            <td>{nameEn}</td>
                            <td>{nameAr || 'N/A'}</td>
                            <td>
                              <span className={`badge ${isActive ? 'bg-success' : 'bg-danger'}`}>
                                {isActive ? "Yes" : "No"}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                {canEdit && (
                                  <button
                                    className="btn btn-info btn-sm"
                                    onClick={() => handleEdit(category)}
                                  >
                                    <i className="bx bx-edit me-1"></i>
                                    Edit
                                  </button>
                                )}
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={() => handleView(category)}
                                  disabled={viewLoadingRow === categoryId}
                                >
                                  {viewLoadingRow === categoryId ? (
                                    <ClipLoader size={16} color="white" />
                                  ) : (
                                    <>
                                      <Eye size={16} color="white" />
                                      <span className="ms-1">View</span>
                                    </>
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                 style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
              <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
              </div>
              {totalCount > pageSize && (
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <Button
                    color="light"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                    style={{
                      border: '1px solid #dee2e6',
                      borderRadius: '0.375rem',
                      minWidth: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                      color: page === 1 ? '#6c757d' : '#495057'
                    }}
                  >
                    <i className="mdi mdi-chevron-left"></i>
                  </Button>
                  
                  {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                    const pageNum = index + 1;
                    const isActive = page === pageNum;
                    
                    return (
                      <Button
                        key={pageNum}
                        color={isActive ? "primary" : "light"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        style={{
                          border: '1px solid #dee2e6',
                          borderRadius: '0.375rem',
                          minWidth: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: isActive ? '#556ee6' : '#fff',
                          borderColor: isActive ? '#556ee6' : '#dee2e6',
                          color: isActive ? '#fff' : '#495057',
                          fontWeight: isActive ? '600' : '400',
                          boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                        }}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  
                  <Button
                    color="light"
                    size="sm"
                    disabled={page === Math.ceil(totalCount / pageSize)}
                    onClick={() => handlePageChange(page + 1)}
                    style={{
                      border: '1px solid #dee2e6',
                      borderRadius: '0.375rem',
                      minWidth: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                      color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                    }}
                  >
                    <i className="mdi mdi-chevron-right"></i>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
